<template>
  <div>
    <div class="bg-white">
      <div class="container">
        <div class="py-3">
          <router-link
            to="/"
            class="d-flex align-items-center text-decoration-none"
          >
            <i class="fas fa-arrow-left text-dark pr-2"></i>
            <span class="text-dark font-weight-bold">Back to Home</span>
          </router-link>
        </div>
        <div v-if="machine">
          <div class="d-flex justify-content-lg-start justify-content-end">
            <h4>{{ machine.Title }}</h4>
          </div>
          <div
            class="d-flex flex-column flex-lg-row justify-content-lg-start justify-content-end pb-3"
          >
            <span class="text-muted flex-sm-row"
              >Stock: {{ machine.StockNumber }}</span
            >
            <span class="text-muted flex-sm-row pl-lg-3">
              Photos:
              <span v-html="photoCount"></span>
            </span>
            <span class="text-muted flex-sm-row pl-lg-3"
              >Last Updated: {{ dateModified(machine.DateModified) }}
              {{ modifiedBy }}
            </span>
            <span
              v-if="unsavedPhotoCount"
              class="flex-sm-row pl-lg-3 font-weight-bold text-danger"
              >{{ unsavedPhotoCount }} unsaved image(s).</span
            >
          </div>
        </div>
      </div>
    </div>

    <div class="container">
      <div
        class="d-flex justify-content-around align-items-center mt-3 py-3 box-shadow bg-white"
        :class="{ 'fixed-bottom mx-3': isMobile }"
      >
        <div class="text-uppercase font-weight-bold">
          <file-upload
            class="btn btn-link btn-no-hover-effect d-inline-flex align-items-center text-decoration-none text-dark font-weight-bold text-uppercase add-file"
            :multiple="true"
            :drop="true"
            :drop-directory="true"
            v-model="photos"
            @input-filter="inputFilter"
            ref="upload"
          >
            <i class="fas fa-plus"></i>Add
          </file-upload>
          <button
            type="button"
            class="btn btn-link btn-no-hover-effect d-none d-lg-inline-flex d-md-none text-decoration-none text-dark font-weight-bold text-uppercase"
            :disabled="enableBulkSelect()"
            v-on:click="bulkSelect = !bulkSelect"
          >
            Bulk Select
          </button>
          <button
            @click="deleteMultipleImages()"
            :disabled="!this.selected.length > 0"
            type="button"
            class="btn btn-link btn-no-hover-effect d-inline-flex text-decoration-none text-dark font-weight-bold text-uppercase"
          >
            Bulk Delete
          </button>
        </div>
        <div class="text-uppercase font-weight-bold">
          <button
            type="button"
            class="btn btn-primary border-0 mb-0 text-decoration-none font-weight-bold text-uppercase titan-red"
            @click="uploadImages"
          >
            Save
          </button>
        </div>
      </div>

      <Search
        v-if="!machine"
        class="mt-3"
        @updateSelectedMachine="updateSelectedMachine($event)"
      />

      <div class="card-columns">
        <draggable
          :list="imagesForCards"
          :delay="100"
          :animation="150"
          :move="updateSortOrder"
          :disabled="draggableDisabled || (photos && photos.length)"
        >
          <div
            class="machine-card"
            v-for="(file, index) in imagesForCards"
            :key="file.id"
          >
            <transition :duration="{ enter: 500, leave: 800 }">
              <ImageCard
                :image="file"
                :machine="machine"
                :loading="loading"
                :index="index"
                :bulkSelect="bulkSelect"
                @updateSelectedImage="addToSelectedImages($event)"
                @deleteSelectedImage="deleteImage($event)"
                @disableDraggableCards="disableDrag($event)"
              />
            </transition>
          </div>
        </draggable>
      </div>

      <div v-if="archiveActive">
        <h5 v-if="archivedImages && archivedImages.length">
          <hr />
          Archived Images
        </h5>
        <div class="card-columns">
          <div
            class="machine-card"
            v-for="(file, index) in archivedImages"
            :key="file.id"
          >
            <ImageCard
              :image="file"
              :machine="machine"
              :loading="loading"
              :index="index"
              :bulkSelect="bulkSelect"
              :isArchived="true"
              @restoreArchivedImage="restoreImage($event)"
            />
          </div>
        </div>
        <div v-if="displayNoPhotos">
          <h3 class="text-center my-3">No Photos</h3>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ImageCard from '@/components/ImageCard';
import Search from '@/components/Search';
import draggable from 'vuedraggable';
import FileUpload from 'vue-upload-component';
import UploaderMixins from '../mixins/UploaderMixins.js';
import EquipmentDataService from '../services/EquipmentDataService';

export default {
  name: 'PhotoUploader',
  mixins: [UploaderMixins],
  components: {
    Search,
    FileUpload,
    draggable,
    ImageCard,
  },
  props: {
    id: {
      type: String,
      required: false,
    },
    files: {
      type: Array,
      required: false,
    },
    passedMachine: {
      type: Object,
      required: false,
    },
  },
  data() {
    return {
      unsavedPhotoCount: null,
      photos: this.files,
      windowWidth: window.innerWidth,
      bulkSelect: false,
      selected: [],
      results: [],
      draggableDisabled: false,
      imagesForCards: [],
      machine: this.passedMachine,
      archivedImages: [],
      authorizedArchiveImageAccounts: [
        'Imageuploaderadmin@titanmachinery.com',
        'jmelikian@mhpteamsi.com',
        'rmusthaq@mhpteamsi.com',
        'kchacko@mhpteamsi.com',
        'lrath@mhpteamsi.com',
        'sclifford@lapero.io',
        'Jordan.Houselog@titanmachinery.com',
        'Ashleigh.Fjelde@titanmachinery.com',
      ],
      loading: false,
    };
  },
  created() {
    Object.entries(localStorage).forEach(([key]) => {
      if (!key.includes('adal')) delete localStorage[key];
    });
    if (!this.passedMachine) {
      this.getMachineData();
    }
    let slug = this.$route.path.substring(6);
    if (this.$route.path.includes('edit')) {
      EquipmentDataService.getArchivedImagesByMachine(slug).then((response) => {
        this.archivedImages = response[0].ImageData;
      });
    }
  },
  mounted() {
    this.getImagesForCards();
    window.addEventListener('resize', () => {
      this.windowWidth = window.innerWidth;
    });
  },
  computed: {
    displayNoPhotos() {
      return Boolean(
        !this.photos && (!this.machine || !this.machine.ImageCount)
      );
    },
    photoCount() {
      return this.photos || this.machine.ImageCount > 0
        ? this.machine.ImageCount
        : '<span class="titan-red-text"> No Photos</span>';
    },
    modifiedBy() {
      return this.machine.ModifiedBy
        ? ' by ' + this.machine.ModifiedBy
        : ' by System';
    },
    archiveActive() {
      var profileEmail = this.$store.state.user.user.email;
      return Boolean(
        this.authorizedArchiveImageAccounts.includes(profileEmail)
      );
    },
  },
  methods: {
    getMachineData() {
      let slug = this.$route.path.substring(6);
      EquipmentDataService.getEquipmentById(slug).then((response) => {
        this.machine = response.Data[0];
      });
    },
    getImagesForCards() {
      if (this.machine && this.machine.ImageData) {
        this.imagesForCards = this.machine.ImageData;
      }

      if (this.photos) {
        this.imagesForCards = this.photos;
      }
    },
    enableBulkSelect() {
      return !((this.machine && this.machine.ImageData) || this.photos);
    },
    updateSortOrder(evt) {
      evt.relatedContext.element.Status = 'U';
      evt.draggedContext.element.Status = 'U';
      evt.draggedContext.element.Resorted = true;
      evt.relatedContext.element.Resorted = true;
      evt.draggedContext.element.SortOrder =
        evt.relatedContext.element.SortOrder;
      evt.relatedContext.element.SortOrder =
        evt.draggedContext.element.SortOrder;
    },
    uploadImages() {
      var sortedImages = [];
      var newImages = [];

      if (
        this.machine &&
        confirm(`Add these images to ${this.machine.Title}?`)
      ) {
        this.imagesForCards.forEach((image) => {
          if (image.Resorted && image.Resorted == true && !image.IsNewFile) {
            sortedImages.push(image);
          } else if (image.IsUploaded == false && image.IsNewFile == true) {
            newImages.push(image);
          }
        });
        if (sortedImages.length) {
          this.uploadExistingImageData(sortedImages);
        }

        if (newImages.length) {
          this.uploadNewImageData(newImages);
        }
      } else
        alert(
          'Please assign the image(s) to a machine using the search below.'
        );
    },
    updateSelectedMachine(val) {
      this.machine = val;
    },
    addToSelectedImages(val) {
      if (!this.selected.includes(val)) {
        this.selected.push(val);
      } else {
        this.selected = this.selected.filter((item) => item.id !== val.id);
      }
    },
    deleteImage(val) {
      if (confirm('Are you sure that you want to delete this image?')) {
        this.deleteSelected(val);
        this.removeImagesFromArray(val);
        let slug = this.$route.path.substring(6);
        EquipmentDataService.getArchivedImagesByMachine(slug).then(
          (response) => {
            this.archivedImages = response[0].ImageData;
          }
        );
      }
    },
    deleteMultipleImages() {
      var imagesToDelete = [];

      this.selected.forEach((image) => {
        imagesToDelete.push(image);
        this.removeImagesFromArray(image);
      });
      this.deleteSelectedImages(imagesToDelete);
    },
    removeImagesFromArray(image) {
      const index = this.imagesForCards.indexOf(image);
      if (index > -1) {
        this.imagesForCards.splice(index, 1);
      }
    },
    isInArray(value, array) {
      return array.indexOf(value) > -1;
    },
    disableDrag(val) {
      this.draggableDisabled = val;
    },
  },
  watch: {
    photos: {
      handler: function() {
        if (this.photos) {
          this.unsavedPhotoCount = this.photos.length;
        }
        this.photos.forEach((element) => {
          if (this.imagesForCards) {
            var checkArrayDups = this.isInArray(element, this.imagesForCards);
            if (checkArrayDups == false) {
              this.imagesForCards.push(element);
            }
          }
        });
      },
    },
    machine: {
      handler: function() {
        if (this.machine && this.machine.ImageData) {
          this.imagesForCards = this.machine.ImageData;
        }
        if (this.photos) {
          this.photos.forEach((element) => {
            this.imagesForCards.push(element);
          });
        }
      },
    },
    imagesForCards: {
      handler: function() {
        if (this.imagesForCards) {
          this.imagesForCards.forEach((image, i) => {
            image.SortOrder = i + 1;
            if (image.SortOrder !== image.PrevSortOrder && !image.IsNewFile) {
              image.Status = 'U';
              image.Resorted = true;
            }
          });
        }
      },
    },
  },
};
</script>
